/**
 * AI Config Hooks — React Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  aiConfigService,
  type UpdateAIConfigInput,
  type OrgProfileInput,
  type AIProviderName,
} from '../services/aiConfig.service';
import { useAuth } from '../context/AuthContext';

export const aiConfigKeys = {
  all: ['ai-config'] as const,
  detail: () => [...aiConfigKeys.all, 'detail'] as const,
};

export const useAIConfig = (enabled: boolean = true) =>
  useQuery({
    queryKey: aiConfigKeys.detail(),
    queryFn: () => aiConfigService.get(),
    enabled,
    staleTime: 1000 * 60 * 5,
  });

/**
 * Single source of truth for AI Coach UI visibility.
 *
 * - `account_admin` / `super_admin`: full config payload, can manage AI settings.
 * - `manager` / `employee`: receive a sanitized `{ enabled, hasKey }` payload so the
 *   UI can render AI Coach features when the tenant has AI turned on.
 * - `viewer` (and unauthenticated): ineligible — components should render nothing.
 */
export const useCanUseAI = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'account_admin' || role === 'super_admin';
  const isEligible = isAdmin || role === 'manager' || role === 'employee';
  const { data: aiConfig, isLoading } = useAIConfig(isEligible);
  return {
    role,
    isAdmin,
    isEligible,
    aiEnabled: aiConfig?.enabled === true,
    aiHasKey: aiConfig?.hasKey === true,
    aiConfig,
    isLoading,
  };
};

export const useUpdateAIConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAIConfigInput) => aiConfigService.update(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiConfigKeys.detail() });
      toast.success('AI configuration saved');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to save AI configuration'),
  });
};

export const useRemoveAIConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => aiConfigService.remove(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiConfigKeys.detail() });
      toast.success('AI configuration removed');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to remove AI configuration'),
  });
};

export const useTestAIConnection = () =>
  useMutation({
    mutationFn: (input?: { provider?: AIProviderName; apiKey?: string; model?: string }) =>
      aiConfigService.test(input),
  });

export const useUpdateOrgProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrgProfileInput) => aiConfigService.updateOrgProfile(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organization'] });
      toast.success('Organization profile updated');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to update organization profile'),
  });
};
